import {Permission} from '@sollapay/enums';
import {Button, Icon, PageHeader} from '@sollapay/ui/components';
import {useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {usePermissions} from '@/features/authentication';
import {PageContent, PageLayout} from '@/layouts';

import {
  TrustAccountAccessFilterButtons,
  TrustAccountCardList,
  TrustAccountGridCardListSkeleton,
  TrustAccountListEmpty,
  TrustAccountListEmptySearch,
  TrustAccountListToolbar,
  TrustAccountTable,
  TrustAccountTableSkeleton
} from '../features/trust-accounts/components';
import {
  FIRM_ACCOUNT_FILTER_FIELDS,
  TRUST_ACCOUNT_SORT_CONFIG
} from '../features/trust-accounts/config';
import {
  useTrustAccountBalancesQuery,
  useTrustAccountCollapsedGroups,
  useTrustAccountListToolbar,
  useTrustAccountListMode,
  useTrustAccountAccessFilter,
  useTrustAccountsQuery
} from '../features/trust-accounts/hooks';
import {
  TrustAccountsPageModals,
  type TrustAccountsPageModalsHandle
} from '../features/trust-accounts/modals';
import {getSharePanelSearch} from '../features/trust-accounts/share';
import {
  getTrustAccountActivationPath,
  getTrustAccountPath,
  resolveSortMode,
  toMemberListItem
} from '../features/trust-accounts/utils';

import type {MemberTrustAccountListHandlers} from '../features/trust-accounts/utils';
import type {FirmTrustAccountListItemDTO} from '@sollapay/types';
import type {FC} from 'react';

const NO_ACCOUNTS: FirmTrustAccountListItemDTO[] = [];

export const TrustAccountListPage: FC = () => {
  const {t} = useTranslation(['trustAccounts', 'common']);
  const {hasPermission} = usePermissions();
  const canCreate = hasPermission(Permission.TRUST_ACCOUNTS_CREATE);
  const {mode} = useTrustAccountListMode();
  const navigate = useNavigate();
  const modalsRef = useRef<TrustAccountsPageModalsHandle | null>(null);
  const {data: accounts = NO_ACCOUNTS, isLoading, error, refetch} = useTrustAccountsQuery();
  const {data: balancesData} = useTrustAccountBalancesQuery();
  const {
    filter: accessFilter,
    setFilter: setAccessFilter,
    counts: accessCounts,
    filtered: accessScopedAccounts,
    isOffered: offersAccessFilter
  } = useTrustAccountAccessFilter(accounts);
  const groupFold = useTrustAccountCollapsedGroups();
  const hasForeignAccounts = useMemo(
    () => accounts.some(account => !account.isTrustee),
    [accounts]
  );
  const sortMode = resolveSortMode({accessFilter, mode, hasForeignAccounts});
  // The balances map, not the query envelope: a refetch with unchanged balances keeps its reference.
  const balancesMap = balancesData?.data;
  const sortConfig = useMemo(
    () => TRUST_ACCOUNT_SORT_CONFIG({balancesMap, mode: sortMode}),
    [balancesMap, sortMode]
  );
  const {toolbar, visibleAccounts, clearSearch, clearFilters} = useTrustAccountListToolbar(
    accessScopedAccounts,
    {sortConfig, filterFields: FIRM_ACCOUNT_FILTER_FIELDS, viewKey: 'trust-accounts:view'}
  );
  const {view, sorting, onSortingChange} = toolbar;
  const items = useMemo(
    () => (visibleAccounts ?? []).map(account => toMemberListItem(account, balancesMap)),
    [visibleAccounts, balancesMap]
  );

  const handleOpenCreate = () => modalsRef.current?.openCreate();
  // Stable, so the table's columns aren't rebuilt on every render.
  const handlers = useMemo<MemberTrustAccountListHandlers>(
    () => ({
      onEditAccount: item => modalsRef.current?.openEdit(item.source),
      onDeleteAccount: item => modalsRef.current?.openDelete(item.source),
      onAccountAction: item => navigate(getTrustAccountPath(item, {tab: '/buyers'})),
      onManageTeam: item =>
        navigate(getTrustAccountPath(item, {search: getSharePanelSearch('people')}))
    }),
    [navigate]
  );
  const listProps = {
    items,
    showRoleBadge: offersAccessFilter && accessFilter === 'all',
    groupFold,
    ...handlers
  };

  const handleCreateSuccess = (id: string) => {
    clearSearch();
    clearFilters();
    navigate(getTrustAccountActivationPath(id));
  };

  return (
    <PageLayout error={error} refetch={() => void refetch()}>
      <PageHeader
        title={t('title')}
        description={t('subtitle')}
        badge={t('accountCount', {count: accounts.length})}
        actions={
          canCreate ? (
            <Button onClick={handleOpenCreate} icon={<Icon name="add" />}>
              {t('create')}
            </Button>
          ) : undefined
        }
      />
      <PageContent>
        {accounts.length === 0 && !isLoading ? (
          <TrustAccountListEmpty
            title={t('noAccountsYet')}
            subtitle={t('noAccountsSubtitle')}
            action={
              canCreate ? (
                <Button onClick={handleOpenCreate} icon={<Icon name="add" />}>
                  {t('create')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div data-slot="trust-account-content" className="flex flex-col gap-6">
            <TrustAccountListToolbar
              {...toolbar}
              leading={
                offersAccessFilter && (
                  <TrustAccountAccessFilterButtons
                    value={accessFilter}
                    counts={accessCounts}
                    onChange={setAccessFilter}
                  />
                )
              }
            />
            {isLoading ? (
              view === 'card' ? (
                <TrustAccountGridCardListSkeleton />
              ) : (
                <TrustAccountTableSkeleton />
              )
            ) : items.length === 0 ? (
              <TrustAccountListEmptySearch
                searchText={toolbar.searchText}
                onClearSearch={clearSearch}
                onClearFilters={() => {
                  clearFilters();
                  setAccessFilter('all');
                }}
              />
            ) : view === 'card' ? (
              <TrustAccountCardList {...listProps} />
            ) : (
              <TrustAccountTable
                {...listProps}
                sorting={sorting}
                onSortingChange={onSortingChange}
              />
            )}
          </div>
        )}
      </PageContent>
      <TrustAccountsPageModals ref={modalsRef} onCreateSuccess={handleCreateSuccess} />
    </PageLayout>
  );
};
